import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { getAuthCookieExpireDays } from '../services/cookieConfigService';
import { organizationsService } from '../services/organizationsService';

const AuthContext = createContext();

// Cookie utility functions
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days ?? getAuthCookieExpireDays()) * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

export function clearGenieConversationSessionStorage() {
  deleteCookie('current_conversation_id');
  deleteCookie('current_conversation_transcript');
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('current_conversation_id');
      sessionStorage.removeItem('current_conversation_transcript');
    }
  } catch {
    /* ignore */
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);

  // Wrapper for setUser that also saves to cookie
  const setUser = (userData) => {
    setUserState(userData);
    if (userData) {
      setCookie('user', userData, getAuthCookieExpireDays());
    } else {
      deleteCookie('user');
    }
  };

  // Check for null user and load from cookie
  useEffect(() => {
    if (!user && !loading) {
      const cookieUser = getCookie('user');
      if (cookieUser) {
        setUser(cookieUser);
        setIsAuthenticated(true);
        setPasswordResetRequired(cookieUser.password_reset || false);
      }
    }
  }, [user, loading]);

  useEffect(() => {
    // Try to get user info - if session cookie exists, it will be sent automatically
    // If not, the request will fail and we'll clear state
    authService.getCurrentUser()
      .then(userData => {
        setUser(userData);
        setIsAuthenticated(true);
        setPasswordResetRequired(userData.password_reset || false);
      })
      .catch(() => {
        // No valid session - clear everything
        deleteCookie('user');
        document.cookie = 'session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
        setUser(null);
        setIsAuthenticated(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      // Check if 2FA is required
      if (response.requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true, 
          email: response.email,
          message: response.message 
        };
      }
      
      // Session cookie is set automatically by the browser
      // Get user data to complete login
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      setPasswordResetRequired(userData.password_reset || false);

      clearGenieConversationSessionStorage();
      await organizationsService.refreshOrganizationsAfterLogin();

      return { success: true, password_reset: userData.password_reset || false };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  };

  const logout = async () => {
    // Call logout endpoint to clear server-side session
    await authService.logout();
    // Clear local state
    deleteCookie('user');
    setUser(null);
    setIsAuthenticated(false);
    setPasswordResetRequired(false);
  };

  const reloadUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setPasswordResetRequired(userData.password_reset || false);
      return userData;
    } catch (e) {
      // If reload fails, try to load from cookie
      const cookieUser = getCookie('user');
      if (cookieUser) {
        setUser(cookieUser);
        setIsAuthenticated(true);
        setPasswordResetRequired(cookieUser.password_reset || false);
        return cookieUser;
      }
      return null;
    }
  }, []);

  // Sessions don't need refreshing - they're managed server-side
  // This function is kept for backward compatibility but does nothing
  const refreshToken = async () => {
    // Check if session is still valid by getting current user
    try {
      await authService.getCurrentUser();
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    passwordResetRequired,
    login,
    logout,
    refreshToken,
    reloadUser,
    setUser,
    setIsAuthenticated,
    setPasswordResetRequired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
